import { createAsyncThunk } from "@reduxjs/toolkit";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Axios } from "api/axios";

export const fetchItems = createAsyncThunk(
  "item/fetchItems",
  async ({
    page,
    pageSize,
    sort,
    order,
    search,
    filters,
  }: {
    page: number;
    pageSize: number;
    search?: string;
    sort?: string;
    order?: "asc" | "desc" | "";
    filters?: any;
  }) => {
    const params = new URLSearchParams();
    params.append("page", page.toString());
    params.append("page_size", pageSize.toString());
    if (search) params.append("search", search);
    if (sort) {
      const orderingValue = order === "desc" ? `-${sort}` : sort;
      params.append("ordering", orderingValue);
    }

    if (filters && filters.length > 0) {
      filters.forEach(
        ({
          filterField,
          filterValue,
        }: {
          filterField: string;
          filterValue: any;
        }) => {
          if (
            filterField &&
            filterValue !== undefined &&
            filterValue !== null
          ) {
            if (typeof filterValue === "object" && filterValue !== null) {
              // Handle cases like { from_date: ..., to_date: ... }
              Object.entries(filterValue).forEach(([key, value]) => {
                if (value !== undefined && value !== null && value !== "") {
                  params.append(key, String(value));
                }
                if (
                  (filterValue.from_date || filterValue.to_date) &&
                  (filterField === "created_at" || filterField === "updated_at")
                ) {
                  if (filterField === "created_at") {
                    filterField = "created";
                  } else {
                    filterField = "updated";
                  }
                  params.append("filter_by", filterField);
                }
              });
            } else {
              // Handle primitive values (string, number)
              params.append(filterField, filterValue);
            }
          }
        }
      );
    }

    const response: any = await Axios.get(`/item/?${params.toString()}`);

    // const response: any = await Axios.get(`/item/`, {
    //   params: {
    //     page,
    //     page_size: pageSize,
    //     search: search || undefined,
    //     filters: filters ? JSON.stringify(filters) : undefined,
    //   },
    // });
    return {
      data: response?.data?.data || [],
      rowCount: response?.data?.count || 0,
    };
  }
);

export const fetchItemById = createAsyncThunk(
  "item/fetchById",
  async (id: string, { rejectWithValue }) => {
    try {
      const response: any = await Axios.get(`/item/${id}/get/`);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch GP by ID");
    }
  }
);

export const fetchItemByDropDown = createAsyncThunk(
  "Item/fetchByDropDown",
  async (_, { rejectWithValue }) => {
    try {
      const response = await Axios.get(`/Item/dropdown/`);
      return response.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to fetch Item list");
    }
  }
);

export const updateItem = createAsyncThunk(
  "item/updateItem",
  async (itemData: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.put(
        `/item/${itemData?.id}/update/`,
        itemData
      );
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update GP");
    }
  }
);

export const updateStatusInItem = createAsyncThunk(
  "item/updateStatusInItem",
  async (itemData: any, { rejectWithValue }) => {
    try {
      const payload = { is_active: itemData.is_active };
      const response: any = await Axios.patch(
        `/item/${itemData.id}/update/`,
        payload
      );

      return response.data.data;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to update GP");
    }
  }
);

export const addItem = createAsyncThunk(
  "item/addItem",
  async (itemData: any, { rejectWithValue }) => {
    try {
      const response: any = await Axios.post(`/item/create/`, itemData);
      return response;
    } catch (err: any) {
      return rejectWithValue(err.response?.data || "Failed to add GP");
    }
  }
);

//Exports csv file
// Exports department CSV file
export const exportItemCSV = createAsyncThunk(
  "item/exportItemCSV",
  async (_, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem("authToken");
      const headers = {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
        Accept: "*/*",
      };
      // Use responseType: 'blob' to get the file as a blob
      const response = await Axios.get("item/?export=csv", {
        headers,
        responseType: "blob",
      });
      // Return the blob and filename (if any)
      return {
        blob: response.data,
        filename:
          response.headers["content-disposition"]
            ?.split("filename=")[1]
            ?.replace(/"/g, "") || "item.csv",
      };
    } catch (err: any) {
      console.error("Export CSV error:", err);
      return rejectWithValue(err.message || "Failed to export CSV");
    }
  }
);

export const exportItemCSV_v2 = async ({
  search,
  filters,
}: {
  search?: string;
  filters?: any;
}) => {
  const params = new URLSearchParams();

  params.append("export", "excel");

  if (search) params.append("search", search);

  if (filters && filters.length > 0) {
    filters.forEach(
      ({
        filterField,
        filterValue,
      }: {
        filterField: string;
        filterValue: any;
      }) => {
        if (filterField && filterValue !== undefined && filterValue !== null) {
          if (typeof filterValue === "object" && filterValue !== null) {
            Object.entries(filterValue).forEach(([key, value]) => {
              if (value !== undefined && value !== null && value !== "") {
                params.append(key, String(value));
              }
              if (
                (filterValue.from_date || filterValue.to_date) &&
                (filterField === "created_at" || filterField === "updated_at")
              ) {
                if (filterField === "created_at") {
                  filterField = "created";
                } else {
                  filterField = "updated";
                }
                params.append("filter_by", filterField);
              }
            });
          } else {
            params.append(filterField, filterValue);
          }
        }
      }
    );
  }

  const response = await Axios.get(`/item/?${params.toString()}`, {
    responseType: "blob",
  });

  return {
    blob: response.data,
    filename:
      response.headers["content-disposition"]
        ?.split("filename=")[1]
        ?.replace(/"/g, "") || "item.csv",
  };
};
